import { Link } from 'react-router'
import { toast } from 'sonner'

import type { NavItem } from '@/app/navigation'
import { cn } from 'cn'

import { NavBadge } from './nav-badge'
import { BottomNavItemContent, bottomNavItemClassName } from './bottom-nav-item'
import { useNavItemActive } from './use-nav-item-active'
import { useNavItems } from './use-nav-items'

/**
 * Mobile navigation (below `md`): a docked bar with an arched top edge.
 * Items share the width equally and scroll sideways if there are ever too many to fit.
 * Only the active item shows its label, so the bar stays clean as more items are added.
 * Items still to come are dimmed and say so when tapped. Page links only — theme and account live in the app bar on mobile.
 */
export function BottomNav() {
  const items = useNavItems()
  return (
    <nav
      aria-label="Main"
      className="fixed inset-x-0 bottom-0 z-40 rounded-t-[50%_1.75rem] bg-deep-purple px-2 pt-1 pb-[env(safe-area-inset-bottom)] shadow-[0_-12px_32px_-12px_rgb(0_0_0/0.18)] md:hidden"
    >
      <div className="no-scrollbar mx-auto flex max-w-lg overflow-x-auto">
        {items.map((item) => (
          <BottomNavLink key={item.to} item={item} />
        ))}
      </div>
    </nav>
  )
}

function BottomNavLink({ item }: { item: NavItem }) {
  const { icon: Icon } = item
  const isActive = useNavItemActive(item)

  if (item.comingSoon) {
    // No tooltips on touch screens, so a tap says why nothing opened.
    return (
      <button
        type="button"
        aria-disabled="true"
        aria-label={`${item.label}, coming soon`}
        onClick={() => toast.info(`${item.label} is coming soon`)}
        className={cn(bottomNavItemClassName, 'opacity-40')}
      >
        <BottomNavItemContent icon={<Icon className="size-[22px]" />} label={item.label} />
      </button>
    )
  }

  return (
    <Link
      to={item.to}
      aria-label={item.label}
      aria-current={isActive ? 'page' : undefined}
      className={bottomNavItemClassName}
    >
      <BottomNavItemContent
        icon={
          <span className="relative">
            <Icon className="size-[22px]" />
            {item.unreadBadge && <NavBadge className="-top-1.5 -right-2.5 ring-deep-purple" />}
          </span>
        }
        label={item.label}
        active={isActive}
      />
    </Link>
  )
}
